/**
 * Board agent panel (F-144): a docked chat beside the grid. Ask for an edit,
 * attach a storyboard, point at a selection — the server answers with a
 * PLAN, the card shows exactly what it will do (one line per op and a
 * before → after preview), and nothing touches the board until Apply.
 *
 * Reversibility, from the person's side:
 *  - Apply = one Ctrl+Z step (the whole plan, like a paste).
 *  - Every applied card keeps a Revert for as long as this board view is
 *    open, usable in any order; a revert is itself one undo step.
 * The mechanism is `apply.ts`; this file is only the conversation.
 *
 * Docked-panel conventions follow docs/08 §5: `useDockedPanelWidth` +
 * `PanelResizeHandle`, `CloseButton`, `data-tip` on icon-only controls.
 */
import { AGENT_MAX_ATTACHMENTS, AGENT_MAX_MESSAGE_CHARS, type AgentPlan, type ImportStepTask } from "@kola/shared";
import { useEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent } from "react";
import { useStore } from "zustand";

import { isMac } from "../../core/keyboard.js";
import type { BoardSession } from "../../core/session.js";
import { CloseButton } from "../../ui/CloseButton.js";
import { iconAgent } from "../../ui/icons.js";
import { PanelResizeHandle } from "../../ui/PanelResizeHandle.js";
import { showToast } from "../../ui/toast.js";
import { useDockedPanelWidth } from "../../ui/useDockedPanelWidth.js";
import { ModelSelect } from "../ai-models/ModelSelect.js";
import { rememberModel } from "../ai-models/pick.js";
import type { AppliedRun } from "./apply.js";
import { AGENT_ATTACHMENT_ACCEPT, attachmentProblem, readAgentAttachment } from "./attachments.js";
import { applyTurn, dismissImportTasks, revertTurn, runImportStep, sendAgentTurn, skipsSuffix } from "./controller.js";
import { describeOps, planDeletes, previewPlan } from "./describe.js";
import { importStepLines } from "./import-steps.js";
import { IMPORT_STEP_LABELS, importStepBlocked } from "./import-tasks.js";
import { agentSelection, describeSelection } from "./selection.js";
import type { AgentPlanState, AgentUiStore } from "./store.js";

export const AGENT_FUNCTION_KEY = "board-agent" as const;

const EXAMPLES = [
  "Import the attached storyboard into this sheet",
  "Add a Duration column after Visual",
  "Which rows mention the kettle?",
  "Clear the selected cells",
];

export interface AgentPanelProps {
  session: BoardSession;
  ui: AgentUiStore;
}

export function AgentPanel({ session, ui }: AgentPanelProps) {
  const panelWidth = useDockedPanelWidth("agent", 360);
  const turns = useStore(ui, (s) => s.turns);
  const pending = useStore(ui, (s) => s.pending);
  const draft = useStore(ui, (s) => s.draft);
  const attachments = useStore(ui, (s) => s.attachments);
  const model = useStore(ui, (s) => s.model);
  const runs = useStore(ui, (s) => s.runs);
  const sheetId = useStore(session.store, (s) => s.sheetId);
  const frozen = useStore(session.store, (s) => s.frozen);
  const localSelection = useStore(session.store, (s) => s.localSelection);
  // Plan cards read the board for their preview; a structure change re-reads.
  const structureVersion = useStore(session.store, (s) => s.structureVersion);
  const [dropping, setDropping] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachSeq = useRef(0);

  useEffect(() => {
    const el = threadRef.current;
    if (el !== null) el.scrollTop = el.scrollHeight;
  }, [turns.length, pending]);

  const selection = useMemo(
    () => (sheetId === null ? undefined : agentSelection(session.board, localSelection, sheetId)),
    [session, localSelection, sheetId],
  );
  const selectionText =
    sheetId === null ? null : describeSelection(selection, session.board, sheetId);

  const attachFiles = async (files: Iterable<File>) => {
    for (const file of files) {
      if (ui.getState().attachments.length >= AGENT_MAX_ATTACHMENTS) {
        showToast(`At most ${AGENT_MAX_ATTACHMENTS} files per message.`);
        return;
      }
      attachSeq.current += 1;
      const outcome = await readAgentAttachment(file, `f${attachSeq.current}`);
      if (outcome.ok) ui.getState().addAttachment(outcome.attachment);
      else showToast(attachmentProblem(outcome.reason, file.name));
    }
  };

  const send = async () => {
    const message = draft.trim();
    if (message.length === 0 || pending || sheetId === null || frozen) return;
    const files = ui.getState().attachments;
    ui.getState().setDraft("");
    ui.getState().clearAttachments();
    await sendAgentTurn(session, ui, { message, attachments: files, selection, model });
    inputRef.current?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void send();
    }
  };

  const onDragOver = (event: DragEvent) => {
    if (!Array.from(event.dataTransfer.types).includes("Files")) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setDropping(true);
  };
  const onDrop = (event: DragEvent) => {
    if (!Array.from(event.dataTransfer.types).includes("Files")) return;
    event.preventDefault();
    event.stopPropagation();
    setDropping(false);
    void attachFiles(Array.from(event.dataTransfer.files));
  };

  const canSend = !pending && !frozen && sheetId !== null && draft.trim().length > 0;

  return (
    <aside
      className={`agent-panel${dropping ? " agent-dropping" : ""}`}
      aria-label="Board agent"
      style={{ width: panelWidth.width }}
      onDragOver={onDragOver}
      onDragLeave={() => setDropping(false)}
      onDrop={onDrop}
    >
      <PanelResizeHandle
        label="Resize the agent panel"
        width={panelWidth.width}
        bounds={panelWidth.bounds}
        onResize={panelWidth.setWidth}
        onCommit={panelWidth.commit}
        onReset={panelWidth.reset}
      />
      <div className="agent-panel-head">
        <span className="agent-panel-icon">{iconAgent}</span>
        <span className="agent-panel-title">Agent</span>
        <span className="spacer" />
        {turns.length > 0 && (
          <button
            type="button"
            className="agent-clear"
            disabled={pending}
            onClick={() => ui.getState().clearConversation()}
            title="Start a new conversation. Applied plans stay applied; Ctrl+Z still undoes them."
          >
            New chat
          </button>
        )}
        <CloseButton label="Close panel" onClick={() => ui.getState().setPanelOpen(false)} />
      </div>

      <div className="agent-thread" ref={threadRef}>
        {turns.length === 0 && (
          <div className="agent-empty">
            <p>
              Ask for an edit, attach a storyboard (.md, .csv, .tsv, .txt), or select cells and say
              what to do with them. Every plan is shown before it lands, and every applied plan can be
              reverted.
            </p>
            <div className="agent-examples">
              {EXAMPLES.map((example) => (
                <button
                  key={example}
                  type="button"
                  className="agent-example"
                  onClick={() => {
                    ui.getState().setDraft(example);
                    inputRef.current?.focus();
                  }}
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        )}
        {turns.map((turn) => (
          <div key={turn.id} className={`agent-turn ${turn.role}${turn.error ? " error" : ""}`}>
            <div className="agent-turn-text">{turn.text}</div>
            {turn.attachments !== undefined && turn.attachments.length > 0 && (
              <div className="agent-turn-files">
                {turn.attachments.map((file) => (
                  <span key={file.name} className="agent-chip" title={`${file.chars} characters`}>
                    {file.name}
                  </span>
                ))}
              </div>
            )}
            {turn.plan !== undefined && turn.planState !== undefined && (
              <PlanCard
                key={structureVersion}
                session={session}
                plan={turn.plan}
                state={turn.planState}
                run={turn.runId === undefined ? undefined : runs[turn.runId]}
                frozen={frozen}
                onApply={() => void applyTurn(session, ui, turn.id)}
                onDiscard={() => ui.getState().updateTurn(turn.id, { planState: "discarded" })}
                onRevert={() => void revertTurn(session, ui, turn.id)}
              />
            )}
            {turn.model !== undefined && <div className="agent-turn-model">{turn.model}</div>}
          </div>
        ))}
        {pending && <div className="agent-pending">Thinking…</div>}
      </div>

      <ImportTasksBar session={session} ui={ui} />

      <div className="agent-composer">
        {attachments.length > 0 && (
          <div className="agent-attachments">
            {attachments.map((file) => (
              <span key={file.id} className="agent-chip">
                {file.name}
                <button
                  type="button"
                  className="agent-chip-remove"
                  aria-label={`Remove ${file.name}`}
                  data-tip="Remove"
                  onClick={() => ui.getState().removeAttachment(file.id)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <textarea
          ref={inputRef}
          className="agent-input"
          rows={3}
          value={draft}
          maxLength={AGENT_MAX_MESSAGE_CHARS}
          placeholder={
            frozen
              ? "Read-only while offline"
              : turns.length === 0
                ? "What should the agent do?"
                : "Reply or ask for more…"
          }
          disabled={pending || frozen}
          onChange={(event) => ui.getState().setDraft(event.target.value)}
          onKeyDown={onKeyDown}
        />
        <div className="agent-actions">
          <input
            ref={fileInputRef}
            type="file"
            accept={AGENT_ATTACHMENT_ACCEPT}
            multiple
            hidden
            onChange={(event) => {
              const files = event.target.files;
              if (files !== null) void attachFiles(Array.from(files));
              event.target.value = "";
            }}
          />
          <button
            type="button"
            className="btn"
            disabled={pending || frozen}
            onClick={() => fileInputRef.current?.click()}
            title="Attach a markdown, CSV, TSV or text file"
          >
            Attach
          </button>
          <label className="agent-model-pick" title="Which model plans">
            <ModelSelect
              functionKey={AGENT_FUNCTION_KEY}
              kind="text"
              value={model}
              disabled={pending}
              onChange={(next) => {
                ui.getState().setModel(next);
                rememberModel(AGENT_FUNCTION_KEY, next);
              }}
            />
          </label>
          <span className="spacer" />
          <button type="button" className="btn primary" disabled={!canSend} onClick={() => void send()}>
            {pending ? "Sending…" : "Send"}
          </button>
        </div>
        <div className="agent-hint">
          {selectionText !== null ? `Selection: ${selectionText}` : "No selection — select cells to point the agent at them."}
        </div>
      </div>
    </aside>
  );
}

/* ------------------------------ import tasks ---------------------------- */

/** The owner's numbering: step 1 (populate) is the import itself. */
const STEP_NUMBER: Record<ImportStepTask, string> = { beats: "2", translate: "3", align: "4" };

/**
 * F-150: what is left of a file import — one button per step. A button
 * proposes its step as a plan in the thread; applying the plan removes the
 * button, discarding it leaves the button to try again.
 */
function ImportTasksBar({ session, ui }: { session: BoardSession; ui: AgentUiStore }) {
  const tasks = useStore(ui, (s) => s.importTasks);
  const pending = useStore(ui, (s) => s.pending);
  const turns = useStore(ui, (s) => s.turns);
  if (tasks === null || tasks.pending.length === 0) return null;
  const awaiting = (step: ImportStepTask) =>
    turns.some((t) => t.importStep === step && (t.planState === "proposed" || t.planState === "applying"));
  return (
    <div className="agent-import-tasks" role="group" aria-label="Import steps">
      <div className="agent-import-tasks-head">
        <span className="agent-import-tasks-title">
          Next steps for <strong>{tasks.fileName}</strong>
        </span>
        <button
          type="button"
          className="agent-import-tasks-dismiss"
          data-tip="Hide the remaining steps"
          onClick={() => dismissImportTasks(session, ui)}
        >
          Dismiss
        </button>
      </div>
      <div className="agent-import-tasks-buttons">
        {tasks.pending.map((step) => {
          const blocked = importStepBlocked(tasks, step);
          const waiting = awaiting(step);
          return (
            <button
              key={step}
              type="button"
              className="btn agent-import-task"
              disabled={pending || blocked !== null || waiting}
              {...(blocked !== null
                ? { "data-tip": blocked }
                : waiting
                  ? { "data-tip": "Apply or discard its plan above first" }
                  : {})}
              onClick={() => void runImportStep(session, ui, step)}
            >
              <span className="agent-import-task-num" aria-hidden>
                {STEP_NUMBER[step]}
              </span>
              {IMPORT_STEP_LABELS[step]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------ plan card ------------------------------- */

interface PlanCardProps {
  session: BoardSession;
  plan: AgentPlan;
  state: AgentPlanState;
  run: AppliedRun | undefined;
  frozen: boolean;
  onApply: () => void;
  onDiscard: () => void;
  onRevert: () => void;
}

function PlanCard({ session, plan, state, run, frozen, onApply, onDiscard, onRevert }: PlanCardProps) {
  const lines = describeOps(session.board, plan);
  const steps = importStepLines(plan);
  const preview = state === "proposed" || state === "applying" ? previewPlan(session.board, plan) : null;
  const cellCount = preview === null ? 0 : preview.rows.length + preview.more;
  const undoKey = isMac ? "⌘Z" : "Ctrl+Z";
  return (
    <div className={`agent-plan ${state}`}>
      <div className="agent-plan-summary">{plan.summary}</div>
      <ul className="agent-plan-ops">
        {lines.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
      {/* F-149: each import step, who handled it and how it went. */}
      {steps.length > 0 && (
        <details className="agent-import-steps" open>
          <summary>Import steps</summary>
          <ul>
            {steps.map((step) => (
              <li key={step.label} className={step.warn ? "agent-import-step-warn" : undefined}>
                <div className="agent-import-step-head">
                  <span className="agent-import-step-label">{step.label}</span>
                  {/* The vendor prefix is noise at this width; the full id is the tooltip. */}
                  <span className="agent-import-step-handler" title={step.handler}>
                    {step.handler.split("/").pop()}
                  </span>
                </div>
                <div className="agent-import-step-detail">{step.detail}</div>
              </li>
            ))}
          </ul>
        </details>
      )}
      {preview !== null && cellCount > 0 && (
        <details className="agent-plan-preview">
          <summary>
            Preview {cellCount} cell {cellCount === 1 ? "change" : "changes"}
          </summary>
          <table>
            <thead>
              <tr>
                <th>Row</th>
                <th>Column</th>
                <th>Before</th>
                <th>After</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((row, i) => (
                <tr key={i}>
                  <td>{row.row}</td>
                  <td>{row.column}</td>
                  <td className="agent-cell-before">{row.before}</td>
                  <td className="agent-cell-after">{row.after}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {preview.more > 0 && <div className="agent-plan-more">…and {preview.more} more</div>}
        </details>
      )}
      {state === "proposed" && planDeletes(plan.ops) && (
        <div className="agent-plan-warn">
          This plan deletes rows or columns. {undoKey} restores them exactly; Revert restores their text only.
        </div>
      )}
      <div className="agent-plan-actions">
        {state === "proposed" && (
          <>
            <button type="button" className="btn primary" disabled={frozen} onClick={onApply}>
              Apply
            </button>
            <button type="button" className="btn" onClick={onDiscard}>
              Discard
            </button>
          </>
        )}
        {state === "applying" && <span className="agent-plan-state">Applying…</span>}
        {state === "applied" && (
          <>
            <span className="agent-plan-state">Applied{run === undefined ? "" : skipsSuffix(run.skipped)}</span>
            <button type="button" className="btn" disabled={frozen} onClick={onRevert}>
              Revert
            </button>
            <span className="agent-plan-hint">or {undoKey}</span>
          </>
        )}
        {state === "reverting" && <span className="agent-plan-state">Reverting…</span>}
        {state === "reverted" && <span className="agent-plan-state">Reverted</span>}
        {state === "discarded" && <span className="agent-plan-state">Discarded</span>}
      </div>
    </div>
  );
}
